import type { Theme } from "@mui/material";
import type { GridEventListener, GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useCallback, useEffect, useMemo } from "react";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import { getVisibleRowIds, selectNexRow, selectPreviousRow } from "@entrilia/shared/utilities/dataGridApiHelper";
import type { DocumentInfo } from "../../../../../api/types/documentCollectionTypes";
import { getCheckedGridSx } from "../../../../common/grid/gridStyles";
import type { InvestorLevelRowModel } from "./documentsTableDataProvider";
import { getInvestorLevelRows, investorLevelColumns } from "./documentsTableDataProvider";

interface Props {
  documents: DocumentInfo[];
  showPublishingStatus: boolean;
  showPortalPublishStatus: boolean;
  investorPortalEnabled: boolean;
  showFundName: boolean;
  onSelectionChange: (selection: GridRowSelectionModel) => void;
}

const DocumentsTableInvestorLevel = ({
  documents,
  showPublishingStatus,
  showPortalPublishStatus,
  investorPortalEnabled,
  showFundName,
  onSelectionChange,
}: Props) => {
  const apiRef = useGridApiRef();

  const rows = useMemo(() => getInvestorLevelRows(documents), [documents]);

  useEffect(() => {
    const firstRowId = getVisibleRowIds(apiRef)[0];
    if (!firstRowId) {
      return;
    }
    const selectTimeoutId = setTimeout(() => {
      apiRef.current?.setRowSelectionModel({ ids: new Set([firstRowId]), type: "include" });
      apiRef.current?.setCellFocus(firstRowId, "investorTitle");
    });
    return () => clearTimeout(selectTimeoutId);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  useEffect(() => {
    return apiRef.current?.subscribeEvent("rowSelectionChange", (newSelection) => {
      onSelectionChange(newSelection);
    });
  }, [apiRef, onSelectionChange]);

  const handleCellKeyDown: GridEventListener<"cellKeyDown"> = useCallback(
    (_, e) => {
      if (e.code !== "ArrowUp" && e.code !== "ArrowDown") {
        return;
      }

      if (e.code === "ArrowDown") {
        selectNexRow(apiRef);
      } else if (e.code === "ArrowUp") {
        selectPreviousRow(apiRef);
      }

      e.stopPropagation();
      e.preventDefault();
    },
    [apiRef]
  );

  const handleRowClick: GridEventListener<"rowClick"> = useCallback(
    (params) => {
      apiRef.current?.setRowSelectionModel({ ids: new Set([params.id]), type: "include" });
    },
    [apiRef]
  );

  const getSx = useCallback(
    (theme: Theme) => ({
      ...getCheckedGridSx(theme),
      ".MuiDataGrid-row": {
        "&:hover": {
          cursor: "pointer",
        },
      },
    }),
    []
  );

  return (
    <DataGrid<InvestorLevelRowModel>
      apiRef={apiRef}
      columns={investorLevelColumns}
      sx={getSx}
      rows={rows}
      onCellKeyDown={handleCellKeyDown}
      onRowClick={handleRowClick}
      checkboxSelection
      disableColumnSorting
      columnVisibilityModel={{
        publishingStatus: showPublishingStatus,
        publishToInvestorPortal: showPortalPublishStatus,
        fundName: showFundName,
        documentAccess: investorPortalEnabled,
        isViewed: investorPortalEnabled,
      }}
    />
  );
};

export default DocumentsTableInvestorLevel;
